// src/utils/export.ts
import xlsx from "node-xlsx";
import { useUserStore } from "@/store/modules/user";
import { formatDateString } from "@/utils/format";
import { format } from "@/utils/common";
import { buildUserInfoUpdatesFromResponse } from "@/utils/user-profile";
import { $t } from "@/locales";
export { backendToUserInfoMapping, buildUserInfoUpdatesFromResponse } from "@/utils/user-profile";

export interface ExportOptions {
    data: any[];
    filename: string;
    fileFormat: "csv" | "xlsx";
}

interface FormattedPaymentData {
    [key: string]: string;
}
export function updateUserInfoFromResponse(
    response: unknown
): void {
    try {
        const userStore = useUserStore();
        const updates = buildUserInfoUpdatesFromResponse(response);

        // Apply all updates at once if there are any changes
        if (Object.keys(updates).length > 0) {
            userStore.updateUserInfo(updates);
        }
    } catch (error) {
        console.error("Error updating user info:", error);
    }
}

const getTranslatedHeaders = () => ({
    id: $t("billing.paymentId"),
    date: $t("common.date"),
    status: $t("usage.status"),
    purchaseItem: $t("billing.purchaseItem"),
    amount: $t("billing.amount"),
    discount: $t("billing.discount"),
    bank_tracking_code: $t("billing.trackingCode"),
    paidAmount: $t("billing.paidAmount"),
    confirmationDate: $t("billing.confirmationDate"),
});

const getStatusTranslation = (status: string): string => {
    switch (status) {
        case "success":
            return $t("usage.success");
        case "pending":
            return $t("usage.pending");
        case "failed":
            return $t("usage.failed");
        default:
            return status;
    }
};

const getPurchaseItemTranslation = (item: string): string => {
    switch (item) {
        case "api":
            return $t("common.credit");
        case "subscription":
            return $t("common.subscription");
        case "credit":
            return $t("common.credit");
        default:
            return "-";
    }
};

const formatExportData = (data: any[]): FormattedPaymentData[] => {
    const headers = getTranslatedHeaders();

    return data.map((item) => ({
        [headers.id]: item.id,
        [headers.date]: formatDateString(item.created_at, "full"),
        [headers.status]: getStatusTranslation(
            item.confirmed ? "success" : item.bank_transaction?.status || "pending"
        ),
        [headers.purchaseItem]: getPurchaseItemTranslation(item.purchase_item),
        [headers.amount]: format(Number(item.amount), 0),
        [headers.discount]: format(Number(item.discount_amount), 0),
        [headers.bank_tracking_code]: item.bank_tracking_code || "N/A",
        [headers.paidAmount]: format(Number(item.paid_amount), 0),
        [headers.confirmationDate]: item.confirmed_at
            ? formatDateString(item.confirmed_at, "full")
            : "-",
    }));
};

const exportToCSV = async (
    data: FormattedPaymentData[],
    filename: string
): Promise<void> => {
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
        headers
            .map((header) => {
                const value = row[header];
                // Properly escape and quote values containing commas or quotes
                return `"${String(value).replace(/"/g, '""')}"`;
            })
            .join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");

    // Create blob and trigger download
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const exportToXLSX = async (
    data: FormattedPaymentData[],
    filename: string
): Promise<void> => {
    const headers = Object.keys(data[0]);

    // Define column widths (in characters)
    const colWidths = headers.map((header) => ({
        wch: Math.max(header.length, 15), // minimum width of 15 characters
    }));

    // Convert data to array format required by node-xlsx
    const rows = data.map((row) => headers.map((header) => row[header]));

    // Create worksheet data
    const worksheetData = [headers, ...rows];

    // Build xlsx with options
    const sheetOptions = {
        "!cols": colWidths,
    };

    const buffer = xlsx.build([
        {
            name: $t("billing.paymentHistory"),
            data: worksheetData,
            options: sheetOptions,
        },
    ]);

    // Create blob and trigger download - convert Buffer to Uint8Array for proper Blob compatibility
    const blob = new Blob([new Uint8Array(buffer)], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const exportData = async ({
    data,
    filename,
    fileFormat,
}: ExportOptions): Promise<void> => {
    try {
        const formattedData = formatExportData(data);

        if (fileFormat === "csv") {
            return exportToCSV(formattedData, filename);
        }
        return exportToXLSX(formattedData, filename);
    } catch (error) {
        console.error(`Error exporting ${fileFormat} file:`, error);
        throw error;
    }
};
